import inView from "../src/utilites/inView.js";
import safeParse from "./utilites/parse.js";
import { toNumber } from "lodash";
import SplitText from "./SplitText.ts";

gsap.registerPlugin(SplitText);

export default class TextAnimations {
    constructor(container = '', el = '.title') {
        this.container = container;
        this.text = this.container.querySelector(el);
        this.settings = safeParse(this.text.dataset.animation);

        this.split = null;
        this.animation = null;
        this.ro = null;
        this.cleanupInView = null;

        this._observe();
        this._bindInView();
    }

    _setup() {
        if (this.split) this.split.revert();
        if (this.animation) this.animation.kill();
        if (this.ro) this.ro.disconnect();

        this.split = SplitText.create(this.text, { type: this.settings.splitType });
    }

    _observe() {
        this.ro = new ResizeObserver(() => this._setup());
        this.ro.observe(this.text);
    }

    _runAnimation() {
        if (this.animation) return;
        
        const elements = this.split[this.settings.splitType || 'chars'];
        
        // Set initial color if fromColor is provided
        if (this.settings.fromColor) {
            gsap.set(elements, { color: this.settings.fromColor });
        }
        
        const animationConfig = {
            rotationX: toNumber(this.settings.rotationX),
            x: toNumber(this.settings.x),
            y: toNumber(this.settings.y),
            transformOrigin: "none",
            opacity: this.settings.opacity,
            duration: toNumber(this.settings.duration) || 1,
            ease: this.settings.ease,
            stagger: toNumber(this.settings.stagger) || 0.05,
            delay: toNumber(this.settings.delay)
        };

        // Add color fill support - animate to target color
        if (this.settings.color) {
            animationConfig.color = this.settings.color;
        }

        // Add infinite move support
        if (this.settings.infinite === true || this.settings.infinite === 'yes') {
            animationConfig.repeat = -1;
            animationConfig.yoyo = this.settings.yoyo !== false && this.settings.yoyo !== 'no';
            
            // Add flip effect on each animation cycle end
            const flipElements = () => {
                const elements = this.split[this.settings.splitType || 'chars'];
                gsap.to(elements, {
                    rotationY: '+=180',
                    duration: 0.3,
                    ease: 'power2.inOut',
                    stagger: toNumber(this.settings.stagger) || 0.05
                });
            };
            
            // Flip on complete (end of forward animation) and on repeat (end of yoyo back)
            animationConfig.onComplete = flipElements;
            animationConfig.onRepeat = flipElements;
        }

        this.animation = gsap.from(
            this.split[this.settings.splitType || 'chars'],
            animationConfig
        );
    }

    _bindInView() {
        this.cleanupInView = inView(
            this.text,
            { threshold: 0.10 },
            { onEnter: () => this._runAnimation() }
        );

    }

    kill() {
        if (this.animation) {
            this.animation.kill();
            this.animation = null;
        }
        if (this.split) {
            this.split.revert();
            this.split = null;
        }
        if (this.ro) {
            this.ro.disconnect();
            this.ro = null;
        }
        if (typeof this.cleanupInView === 'function') {
            this.cleanupInView();
            this.cleanupInView = null;
        }
    }

    restart() {
        if (this.animation) {
            this.animation.kill();
            this.animation = null;
        }
        if (this.split) {
            this.split.revert();
            this.split = null;
        }
        this.split = new SplitText(this.text, { type: this.settings.splitType });
        this._runAnimation();
    }
}
